import { useEffect, useRef, useState } from "react";
import type { ProductInfoCreative } from "@/feature/ads/mock/productsInfoCreative.mock";
import { uploadImagesToS3 } from "@/services/s3/s3Service";
import { toBase64 } from "@/utils/utils";
import { useAuth } from "@/context/AuthContext";
import type { Language } from "@/utils/dictionary";
import { Step1SelectProduct } from "./steps/Step1SelectProduct";
import { Step2AdStudio } from "./steps/Step2AdStudio";

type Step = 1 | 2 | 3;

interface CreateAdWithIAModalProps {
  open: boolean;
  onClose: () => void;
  initialStep?: Step;
  initialProduct?: ProductInfoCreative | null;
}

export function CreateAdWithIAModal({
  open,
  onClose,
  initialStep = 1,
  initialProduct = null,
}: CreateAdWithIAModalProps) {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const { language } = useAuth();
  const productImageInputRef = useRef<HTMLInputElement | null>(null);
  const [step, setStep] = useState<Step>(initialStep);
  const [selectedProduct, setSelectedProduct] = useState<ProductInfoCreative | null>(
    initialProduct
  );
  const [prompt, setPrompt] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [uploadedProductImageUrl, setUploadedProductImageUrl] = useState<string | null>(
    null
  );
  const [isUploadingProductImage, setIsUploadingProductImage] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(initialStep);
    setSelectedProduct(initialProduct);
    setPrompt("");
    setSubmitted(false);
    setUploadedProductImageUrl(null);
    setIsUploadingProductImage(false);
  }, [open, initialStep, initialProduct]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose]);

  if (!open) return null;

  const productName = selectedProduct?.name ?? "";
  const productDescription = selectedProduct?.description ?? "";
  const productImageUrl = uploadedProductImageUrl ?? selectedProduct?.image_url ?? "";
  const canGenerate = Boolean(selectedProduct) && Boolean(prompt.trim()) && !isUploadingProductImage;

  const handleSelectProductImage = () => {
    productImageInputRef.current?.click();
  };

  const handleProductImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      console.error("Solo se permiten imágenes.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      console.error("La imagen debe ser menor a 5MB.");
      return;
    }

    setIsUploadingProductImage(true);
    try {
      const base64File = await toBase64(file);
      const res = await uploadImagesToS3({
        file: base64File,
        folder: "product-images",
        filename: `${Date.now()}-${file.name}`,
      });
      const url = res?.s3_url ?? res?.url ?? res;
      if (typeof url === "string" && url.length > 0) {
        setUploadedProductImageUrl(url);
      } else {
        console.error("Respuesta inesperada al subir imagen:", res);
      }
    } catch (err) {
      console.error("Error al subir la imagen a S3:", err);
    } finally {
      setIsUploadingProductImage(false);
    }
  };

  const handleGenerate = () => {
    setSubmitted(true);
    if (!prompt.trim()) return;

  };

  const handleGenerateImages = (
    generateData: {
      product_name: string;
      product_image: string;
      product_description: string;
      user_prompt: string;
      image_template: string;
      format: string;
      config: {
        language: string;
      };
    }[]
  ) => {
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70" />

      <div
        ref={modalRef}
        className={`relative bg-white rounded-2xl shadow-xl overflow-hidden ${
          step === 2 ? "w-full max-w-5xl h-[90vh]" : "w-full max-w-xl"
        }`}
      >
        {step === 1 ? (
          <Step1SelectProduct
            language={language as Language}
            onClose={onClose}
            onSelectProduct={(product) => {
              setSelectedProduct(product);
              setStep(2);
            }}
          />
        ) : null}
        {step === 2 && selectedProduct ? (
          <Step2AdStudio
            product={selectedProduct}
            onClose={onClose}
            onGenerate={handleGenerateImages}
          />
        ) : null}
      </div>
    </div>
  );
}


